import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { Calendar, Clock, Mail, MapPin, Phone, User, Users, School, BookOpen, ChevronLeft, IdCard } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

const StudentProfile = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { t } = useTranslation('studentprofile');
  const [student, setStudent] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch student data
        const studentResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/students/${studentId}`);
        
        // Fetch classes and streams data
        const classesResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
        
        setStudent(studentResponse.data);
        setClasses(classesResponse.data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId, api, user.school_reg]);

  // Function to get class name from class ID
  const getClassName = (classId) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj ? classObj.name : 'N/A';
  };

  // Function to get stream name from stream ID
  const getStreamName = (streamId) => {
    for (const classObj of classes) {
      const stream = classObj.streams.find(s => s.id === streamId);
      if (stream) {
        return stream.name;
      }
    }
    return 'N/A';
  };

  const getRandomColor = (name) => {
    const colors = [
      'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600', 
      'bg-emerald-600', 'bg-yellow-600', 'bg-cyan-600', 'bg-indigo-600', 
      'bg-rose-600', 'bg-green-600'
    ];
    const index = name.split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-lg">{t('errors.general')}: {error}</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500 text-lg">{t('errors.notFound')}</div>
      </div>
    );
  }

  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';

  return (
    <div className={`container mx-auto px-4 py-8 max-w-7xl ${rtlClass}`}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>{t('navigation.backToStudents')}</span>
        </button>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(`/edit-student/${studentId}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('buttons.editProfile')}
          </button>
          <button
            onClick={() => navigate(`/student-attendance-report/${studentId}`)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {t('buttons.attendanceSummary')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Student Basic Info Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('sections.studentProfile')}</h2>
          </div>
          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              {student.photo ? (
                <img
                  src={student.photo}
                  alt={student.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                />
              ) : (
                <div className={`w-32 h-32 rounded-full flex items-center justify-center text-3xl text-white ${getRandomColor(student.name)}`}>
                  {student.name.charAt(0)}
                </div>
              )}
              <h2 className="text-2xl font-bold mt-4 text-gray-800">{student.name}</h2>
              <p className="text-gray-600">{t('labels.admissionNo')}: {student.admission_number}</p>
            </div>
          </div>
        </div>

        {/* Academic Info Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('sections.academicInformation')}</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem icon={School} label={t('labels.class')} value={getClassName(student.class_id)} />
              <InfoItem icon={BookOpen} label={t('labels.stream')} value={getStreamName(student.stream_id)} />
              <InfoItem icon={User} label={t('labels.gender')} value={student.gender} />
              <InfoItem 
                icon={Calendar} 
                label={t('labels.dateOfBirth')} 
                value={format(new Date(student.date_of_birth), 'dd MMM yyyy')} 
              />
              <InfoItem 
                icon={Clock} 
                label={t('labels.dateOfJoining')} 
                value={format(new Date(student.date_of_joining), 'dd MMM yyyy')} 
              />
              <InfoItem icon={MapPin} label={t('labels.address')} value={student.address} />
            </div>
          </div>
        </div>

        {/* Parent Information Card - Full Width */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden lg:col-span-2">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('sections.parentInformation')}</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoItem icon={Users} label={t('labels.parentName')} value={student.parent_name} />
              <InfoItem icon={IdCard} label={t('labels.idNumber')} value={student.parent_id_number} />
              <InfoItem icon={User} label={t('labels.relation')} value={student.relation_type} />
              <InfoItem icon={Phone} label={t('labels.phone')} value={student.parent_phone} />
              <InfoItem icon={Mail} label={t('labels.email')} value={student.parent_email} />
              <InfoItem icon={MapPin} label={t('labels.address')} value={student.parent_address} />
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate(`/parent-profile/${student.parent_id}`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('buttons.viewParentProfile')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for info items
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-gray-500 mt-1" />
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-base font-medium text-gray-800">{value || 'N/A'}</p>
    </div>
  </div>
);

export default StudentProfile;